import styled from 'styled-components';
import { PropsWithChildren, useCallback } from 'react';
import { Rfq } from '@paradigm/features/src/unified-rfqs/entities/rfq';
import useCreateMarket from '@paradigm/features/src/unified-rfqs/ui/hooks/useCreateMarket';
import useCreateMarketTradeTape from '@paradigm/features/src/unified-rfqs/ui/hooks/useCreateMarketTradeTape';
import {
  AuctionType,
  TradeTapeItem,
} from '@paradigm/features/src/unified-rfqs/entities/shared';
import { useUMPlatformCreationDisabled } from '@paradigm/features/src/platform-state/contexts/providers';
import { Maybe } from '@paradigm/utils/types';
import { useTrackAction } from '@paradigm/features/src/analytics';
import { isProductCodeDisabledForType } from '@paradigm/features/src/products/domain';
import { TOAST_TIMEOUT_LONG, toast } from '../toast';
import PillButton from './PillButton';
import MatchQuoteButton from './MatchQuoteButton';

interface Props {
  type: 'OB' | 'RFQ';
  baseDDTag?: string;
  prefillAccount?: Maybe<string>;
  productCode?: Maybe<string>;
  rfqId: Rfq['id'];
  showDrafts?: Maybe<boolean>;
}

const onError = (message: string) =>
  toast(message, { autoClose: TOAST_TIMEOUT_LONG });

export function QuickCreateButton({
  type,
  rfqId,
  baseDDTag,
  prefillAccount,
  productCode,
  showDrafts,
}: Props) {
  const { createMarket } = useCreateMarket(showDrafts);
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  const trackAction = useTrackAction();
  const isDisabledForType = isProductCodeDisabledForType(productCode, type);

  const onCreateMarket = useCallback(() => {
    trackAction('create market', { auctionType: type });
    createMarket({ rfqId, type, onError, prefillAccount });
  }, [createMarket, prefillAccount, rfqId, trackAction, type]);

  return (
    <PillButton
      ariaLabel={`${baseDDTag} Create ${type}`}
      data-dd-action-name={`${baseDDTag} Create ${type}`}
      onClick={onCreateMarket}
      disabled={isPlatformCreationDisabled || isDisabledForType}
    >
      {type}
    </PillButton>
  );
}

export function QuickActions({
  rfqId,
  baseDDTag,
  prefillAccount,
  productCode,
  showDrafts,
  children,
}: PropsWithChildren<Omit<Props, 'type'>>) {
  return (
    <ActionsContainer>
      <QuickCreateButton
        baseDDTag={baseDDTag}
        prefillAccount={prefillAccount}
        productCode={productCode}
        type="RFQ"
        rfqId={rfqId}
        showDrafts={showDrafts}
      />
      <QuickCreateButton
        baseDDTag={baseDDTag}
        prefillAccount={prefillAccount}
        productCode={productCode}
        type="OB"
        rfqId={rfqId}
        showDrafts={showDrafts}
      />
      {children}
    </ActionsContainer>
  );
}

const baseDDTag = 'Trade Tape Quick Actions';

export function QuickActionsTradeTape({
  trade,
  productCode,
}: {
  trade: TradeTapeItem;
  productCode: Maybe<string>;
}) {
  const { createMarket: createRfqFromTrade } = useCreateMarketTradeTape();
  const platformCreationDisabled = useUMPlatformCreationDisabled();
  const type = trade.rfqType === 'grfq' ? 'RFQ' : 'OB';
  const trackAction = useTrackAction();
  const handleCreateRfq = useCallback(
    (auctionType: AuctionType) => {
      if (trade.rfqType === 'grfq') return;
      trackAction('create market', { auctionType });
      createRfqFromTrade(auctionType, trade);
    },
    [createRfqFromTrade, trackAction, trade],
  );

  if (trade.rfqType === 'grfq') {
    return (
      <QuickActions
        baseDDTag={baseDDTag}
        rfqId={trade.rfq_id}
        productCode={productCode}
      >
        <MatchQuoteButton trade={trade} productCode={productCode} />
      </QuickActions>
    );
  }

  return (
    <ActionsContainer>
      <PillButton
        disabled={
          platformCreationDisabled ||
          isProductCodeDisabledForType(productCode, 'RFQ')
        }
        data-dd-action-name={`${baseDDTag} Create ${type}`}
        ariaLabel={`${baseDDTag} Create ${type}`}
        onClick={() => handleCreateRfq('RFQ')}
      >
        RFQ
      </PillButton>
      <PillButton
        disabled={
          platformCreationDisabled ||
          isProductCodeDisabledForType(productCode, 'OB')
        }
        data-dd-action-name={`${baseDDTag} Create ${type}`}
        ariaLabel={`${baseDDTag} Create ${type}`}
        onClick={() => handleCreateRfq('OB')}
      >
        OB
      </PillButton>
      <MatchQuoteButton trade={trade} productCode={productCode} />
    </ActionsContainer>
  );
}

export const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;

  button {
    white-space: nowrap;
  }
`;
